import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Briefcase, Loader2, Plus, Sprout, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import fertiliserService from '../../services/fertiliser';
import soilService from '../../services/soil';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import {
  FERT_NUTRIENTS, METHOD_LABEL, MISSING_TEXT, RATE_UNITS, TRACE, addNutrients, lineNutrients, nutrientText,
  nutrientsFromAmount, quantityFor, round,
} from '../../utils/fertiliser';
import { maintenance } from '../../utils/soilAnalytes';
import type {
  FertAnalysis, FertiliserPlan, FertiliserProduct, FertMethod, FertNutrient, FertRateUnit, NutrientMap, PlanLine,
} from '../../types/fertiliser';

interface LineDraft {
  key: string;
  id?: number;
  chemical_id: string;
  rate: string;
  rate_unit: FertRateUnit;
  method: FertMethod;
  planned_on: string;
  notes: string;
  saved?: PlanLine;
}

const inputCls = 'w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const seasonText = (y: number) => `${y}–${String(y + 1).slice(2)}`;
let seq = 0;
const newKey = () => `l${(seq += 1)}`;

const toDraft = (l: PlanLine): LineDraft => ({
  key: `s${l.id}`, id: l.id, chemical_id: String(l.chemical_id), rate: String(l.rate), rate_unit: l.rate_unit,
  method: l.method, planned_on: l.planned_on ?? '', notes: l.notes ?? '', saved: l,
});

/** What a job used, per hectare, in kg or L. */
function usedPerHa(l: PlanLine, ha: number | null): { amount: number; unit: 'kg' | 'L' } | null {
  if (l.used_quantity == null || !l.used_unit || !(ha && ha > 0)) return null;
  const u = l.used_unit;
  const base = u === 'g' || u === 'mL' ? l.used_quantity / 1000 : l.used_quantity;
  return { amount: base / ha, unit: u === 'L' || u === 'mL' ? 'L' : 'kg' };
}

/**
 * A planting's fertiliser plan for a season: nutrient targets per hectare
 * (typed, or the maintenance amounts from crop removal), and the
 * applications that meet them: product, rate, method, date. Each line shows
 * what it delivers and how much product the planting takes; a saved line
 * becomes a farm job, and what the job used comes back as applied.
 */
export default function FertiliserPlanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const { hasAccess: canMakeJobs } = useModuleLicense('farm_jobs', ['creator', 'admin']);
  const [plan, setPlan] = useState<FertiliserPlan | null>(null);
  const [products, setProducts] = useState<FertiliserProduct[]>([]);
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const apply = useCallback((p: FertiliserPlan) => {
    setPlan(p);
    setTargets(Object.fromEntries(FERT_NUTRIENTS.map((n) => [n.key, p.targets[n.key] != null ? String(p.targets[n.key]) : ''])));
    setNotes(p.notes ?? '');
    setLines(p.lines.map(toDraft));
    setDirty(false);
  }, []);

  useEffect(() => {
    Promise.all([fertiliserService.plan(Number(id)), fertiliserService.products(true)])
      .then(([p, pr]) => {
        if (pr.success && pr.data) setProducts(pr.data);
        if (p.success && p.data) apply(p.data);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the plan')));
  }, [id, apply]);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const ha = plan?.area_hectares ?? null;
  const trees = plan?.tree_count ?? null;

  const productFor = (l: LineDraft): (FertAnalysis & { product_name: string }) | null =>
    byId.get(Number(l.chemical_id)) ?? (l.saved ? l.saved : null);

  const computed = lines.map((l) => {
    const p = productFor(l);
    const rate = Number(l.rate);
    return {
      line: l,
      product: p,
      nutrients: p ? lineNutrients(p, { rate, rate_unit: l.rate_unit }, ha, trees) : null,
      quantity: quantityFor(rate, l.rate_unit, ha, trees),
    };
  });
  const planned = addNutrients(computed.map((c) => c.nutrients?.nutrients ?? {}));
  const applied = addNutrients((plan?.lines ?? []).map((l) => {
    const u = usedPerHa(l, ha);
    return u ? nutrientsFromAmount(l, u.amount, u.unit).nutrients : {};
  }));

  // Product needed for what is not yet taken off stock, against what is held.
  const needs = (() => {
    const m = new Map<number, { name: string; unit: string; need: number; held: number }>();
    for (const c of computed) {
      if (!c.product || c.quantity.quantity == null || c.line.saved?.used_recorded_at) continue;
      const pid = Number(c.line.chemical_id);
      const prod = byId.get(pid);
      const held = prod?.stock.find((s) => s.unit === c.quantity.unit)?.quantity ?? 0;
      const e = m.get(pid) ?? { name: c.product.product_name, unit: c.quantity.unit, need: 0, held };
      e.need += c.quantity.quantity;
      m.set(pid, e);
    }
    return [...m.values()];
  })();

  const edit = (key: string, patch: Partial<LineDraft>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  const useMaintenance = async () => {
    if (!plan) return;
    setBusy('maintenance');
    setError(null);
    try {
      const r = await soilService.getMaintenance(plan.cultivar_area_id);
      if (!r.success || !r.data) throw r;
      const m = r.data;
      const y = m.yields[0];
      if (!y) { setError('There is no crop estimate or past harvest for this planting to work from. Type the targets.'); return; }
      if (!Object.keys(m.rates).length) { setError('No crop removal figures are set for this crop yet. Add them on Soil and leaf analysis → Crop figures.'); return; }
      const next = { ...targets };
      for (const l of maintenance(m.rates, y.t_per_ha, m.area.area_hectares)) {
        if (l.per_ha == null) continue;
        next[l.nutrient] = String(round(l.unit === 'g/ha' ? l.per_ha / 1000 : l.per_ha, 3));
      }
      setTargets(next);
      setDirty(true);
      setMessage(`Targets set to what a ${y.t_per_ha} t/ha crop takes off (${y.source === 'estimate' ? 'crop estimate' : 'harvest'} ${seasonText(y.season_year)}). Save to keep them.`);
    } catch (e) {
      setError(errorMessage(e, 'Could not read the maintenance amounts'));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!plan) return;
    setError(null);
    setMessage(null);
    const t: NutrientMap = {};
    for (const n of FERT_NUTRIENTS) {
      const v = targets[n.key]?.trim();
      if (!v) continue;
      const num = Number(v);
      if (!(num >= 0)) { setError(`The ${n.label.toLowerCase()} target is not a number.`); return; }
      t[n.key] = num;
    }
    for (const l of lines) {
      if (!l.chemical_id || !(Number(l.rate) > 0)) { setError('Every application needs a fertiliser and a rate.'); return; }
    }
    setBusy('save');
    try {
      const r = await fertiliserService.savePlan(plan.id, {
        targets: t,
        notes: notes.trim() || null,
        lines: lines.map((l) => ({
          id: l.id, chemical_id: Number(l.chemical_id), rate: Number(l.rate), rate_unit: l.rate_unit,
          method: l.method, planned_on: l.planned_on || null, notes: l.notes.trim() || null,
        })),
      });
      if (!r.success || !r.data) throw r;
      apply(r.data);
      setMessage('Saved.');
    } catch (e) {
      setError(errorMessage(e, 'Could not save the plan'));
    } finally {
      setBusy(null);
    }
  };

  const makeJob = async (lineId: number) => {
    setBusy(`job${lineId}`);
    setError(null);
    try {
      const r = await fertiliserService.makeJob(lineId);
      if (!r.success || !r.data) throw r;
      if (r.data.plan) apply(r.data.plan);
      setMessage('Job made. Open it to assign people and a machine.');
    } catch (e) {
      setError(errorMessage(e, 'Could not make the job'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!plan || !window.confirm('Delete this plan?')) return;
    try {
      await fertiliserService.deletePlan(plan.id);
      navigate('/orchard/fertiliser', { replace: true });
    } catch (e) {
      setError(errorMessage(e, 'Could not delete the plan'));
    }
  };

  if (!plan) {
    return error
      ? <div className="max-w-3xl mx-auto p-6"><p className="text-sm text-error-700">{error}</p></div>
      : <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  }

  const shownNutrients = FERT_NUTRIENTS.filter((n) =>
    ['N', 'P', 'K'].includes(n.key) || targets[n.key]?.trim() || planned[n.key] || applied[n.key]);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Fertiliser plan ${seasonText(plan.season_year)}`}
        subtitle={`${plan.farm_name} › ${plan.block_name ? `${plan.block_name} › ` : ''}${plan.cultivar_area_name}`}
        icon={<Sprout className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={
          <div className="flex gap-2">
            <Button size="sm" variant="primary" isLoading={busy === 'save'} disabled={!dirty} onClick={save}>Save plan</Button>
            <Button size="sm" variant="danger" aria-label="Delete plan" onClick={remove}><Trash2 className="h-4 w-4" /></Button>
          </div>
        }
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {error && <div className="bg-error-50 border border-error-200 rounded-lg p-3"><p className="text-sm text-error-700">{error}</p></div>}
        {message && <p className="text-sm text-green-700">{message}</p>}
        <p className="text-xs text-neutral-500">
          {round(ha, 2) ?? '—'} ha{trees ? `, ${trees.toLocaleString()} trees` : ''}. Elemental N, P and K, as fertiliser labels and crop removal give them.
          A plan is your own arithmetic, not a recommendation.
        </p>

        {/* Targets and how the plan stands against them */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-700">What each hectare needs</h3>
                <p className="text-xs text-neutral-500">kg per hectare for the season; trace elements in kg too (0.2 kg is 200 g).</p>
              </div>
              <Button size="sm" variant="outline" isLoading={busy === 'maintenance'} onClick={useMaintenance}>Use maintenance amounts</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500">
                    <th className="font-medium py-1 pr-2">Nutrient</th>
                    <th className="font-medium py-1 px-2 text-right">Target (kg/ha)</th>
                    <th className="font-medium py-1 px-2 text-right">Planned</th>
                    <th className="font-medium py-1 pl-2 text-right">Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {shownNutrients.map((n) => {
                    const t = Number(targets[n.key]);
                    const p = planned[n.key] ?? 0;
                    const short = targets[n.key]?.trim() && t > 0 && p < t * 0.95;
                    return (
                      <tr key={n.key} className="border-t border-neutral-100">
                        <td className="py-1 pr-2 text-neutral-800">{n.label}</td>
                        <td className="py-1 px-2">
                          <input type="text" inputMode="decimal" aria-label={`${n.label} target`}
                            className={cn(inputCls, 'w-20 ml-auto block text-right')}
                            value={targets[n.key] ?? ''} onChange={(e) => { setTargets((x) => ({ ...x, [n.key]: e.target.value })); setDirty(true); }} />
                        </td>
                        <td className={cn('py-1 px-2 text-right tabular-nums whitespace-nowrap', short ? 'text-amber-700' : 'text-neutral-900')}>
                          {nutrientText(n.key, p)}
                        </td>
                        <td className="py-1 pl-2 text-right tabular-nums whitespace-nowrap text-neutral-600">
                          {applied[n.key] != null ? nutrientText(n.key, applied[n.key]) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {FERT_NUTRIENTS.some((n) => !shownNutrients.includes(n)) && (
              <p className="text-xs text-neutral-500">Other nutrients show once a target or an application gives them. Type one into a product's analysis or use the maintenance amounts.</p>
            )}
          </CardContent>
        </Card>

        {/* Applications */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-700">Applications</h3>
            <Button size="sm" variant="outline" onClick={() => {
              setLines((ls) => [...ls, { key: newKey(), chemical_id: '', rate: '', rate_unit: 'kg/ha', method: 'broadcast', planned_on: '', notes: '' }]);
              setDirty(true);
            }}>
              <Plus className="h-4 w-4" />
              Add an application
            </Button>
          </div>
          {products.length === 0 && (
            <p className="text-sm text-neutral-500">
              No fertilisers yet. <button type="button" className="text-primary-600 hover:underline" onClick={() => navigate('/orchard/fertiliser?tab=products')}>Add your products</button> first.
            </p>
          )}
          {computed.map(({ line: l, nutrients, quantity }) => {
            const s = l.saved;
            const fixed = !!s?.job_id;
            return (
              <Card key={l.key}>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
                    <label className="block text-sm">
                      <span className="text-neutral-700">Fertiliser</span>
                      <select className={inputCls} value={l.chemical_id} disabled={fixed} onChange={(e) => edit(l.key, { chemical_id: e.target.value })}>
                        <option value="">Choose</option>
                        {products.filter((p) => p.is_active || String(p.id) === l.chemical_id).map((p) => (
                          <option key={p.id} value={p.id}>{p.product_name}</option>
                        ))}
                      </select>
                    </label>
                    <div className="grid grid-cols-[1fr_6rem] gap-2">
                      <label className="block text-sm">
                        <span className="text-neutral-700">Rate</span>
                        <input type="text" inputMode="decimal" className={inputCls} value={l.rate} disabled={fixed} onChange={(e) => edit(l.key, { rate: e.target.value })} />
                      </label>
                      <label className="block text-sm">
                        <span className="text-neutral-700">per</span>
                        <select className={inputCls} value={l.rate_unit} disabled={fixed} onChange={(e) => edit(l.key, { rate_unit: e.target.value as FertRateUnit })}>
                          {RATE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </label>
                    </div>
                    <label className="block text-sm">
                      <span className="text-neutral-700">How</span>
                      <select className={inputCls} value={l.method} onChange={(e) => edit(l.key, { method: e.target.value as FertMethod })}>
                        {(Object.keys(METHOD_LABEL) as FertMethod[]).map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
                      </select>
                    </label>
                    <label className="block text-sm">
                      <span className="text-neutral-700">When</span>
                      <input type="date" className={inputCls} value={l.planned_on} onChange={(e) => edit(l.key, { planned_on: e.target.value })} />
                    </label>
                    <label className="block text-sm sm:col-span-2">
                      <span className="text-neutral-700">Notes</span>
                      <input type="text" className={inputCls} value={l.notes} onChange={(e) => edit(l.key, { notes: e.target.value })} />
                    </label>
                  </div>

                  <p className="text-xs text-neutral-600 flex flex-wrap gap-x-3 gap-y-1">
                    {quantity.quantity != null
                      ? <span>Takes {round(quantity.quantity, 1)} {quantity.unit} over the planting</span>
                      : quantity.missing && quantity.missing !== 'rate' && <span className="text-amber-700">Amount unknown: {MISSING_TEXT[quantity.missing]}</span>}
                    {nutrients && !nutrients.missing && (Object.entries(nutrients.nutrients) as [FertNutrient, number][]).map(([n, v]) => (
                      <span key={n}>{n} {TRACE.has(n) ? `${round(v * 1000, 0)} g` : `${round(v, 1)} kg`}/ha</span>
                    ))}
                    {nutrients?.missing === 'density' && <span className="text-amber-700">{MISSING_TEXT.density}</span>}
                  </p>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-neutral-600 min-w-0">
                      {s?.job_id ? (
                        <span>
                          <button type="button" className="text-primary-600 hover:underline" onClick={() => navigate(`/jobs/${s.job_id}`)}>
                            Job: {s.job_status ?? 'made'}
                          </button>
                          {s.used_recorded_at && s.used_quantity != null && (
                            <> · used {round(s.used_quantity, 1)} {s.used_unit}{s.planned_on ? `, planned ${formatLocalDateInTz(s.planned_on, timezone)}` : ''}</>
                          )}
                        </span>
                      ) : s ? (
                        canMakeJobs
                          ? <Button size="sm" variant="outline" disabled={dirty} isLoading={busy === `job${s.id}`} onClick={() => makeJob(s.id)}>
                              <Briefcase className="h-4 w-4" />
                              Make job
                            </Button>
                          : <span>A Farm Jobs Creator or Admin seat makes the job.</span>
                      ) : <span>Save the plan, then make the job.</span>}
                      {s && !s.job_id && dirty && canMakeJobs && <span className="ml-2">Save first.</span>}
                    </div>
                    {!fixed && (
                      <Button size="sm" variant="ghost" aria-label="Remove application" onClick={() => { setLines((ls) => ls.filter((x) => x.key !== l.key)); setDirty(true); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {needs.length > 0 && (
          <Card>
            <CardContent className="p-5 space-y-2">
              <h3 className="text-sm font-semibold text-neutral-700">Product still to go on</h3>
              <ul className="divide-y divide-neutral-100">
                {needs.map((n) => (
                  <li key={n.name} className="py-1.5 flex flex-wrap justify-between gap-x-3 text-sm">
                    <span className="text-neutral-800 break-words min-w-0">{n.name}</span>
                    <span className={cn('tabular-nums', n.held < n.need ? 'text-amber-700' : 'text-neutral-700')}>
                      {round(n.need, 1)} {n.unit} needed · {round(n.held, 1)} {n.unit} in stock
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-neutral-500">Stock is every store's, for every planting. Buy more on a purchase order with a Chemical line naming the fertiliser.</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-5">
            <label className="block text-sm">
              <span className="text-neutral-700">Notes</span>
              <textarea className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" rows={3} value={notes}
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }} />
            </label>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button variant="primary" isLoading={busy === 'save'} disabled={!dirty} onClick={save}>Save plan</Button>
        </div>
      </div>
    </div>
  );
}
